import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Lion } from '@/components/brand/Lion'
import { cn } from '@/lib/cn'
import { formatDateLong, formatDateShort, formatDayOf, today } from '@/lib/dates'
import { formatMoney } from '@/lib/format'
import { applyPayments, formatInvoiceNumber, nextDue, outstanding } from '@/lib/invoicing'
import { downloadFile, fileName, formatBytes, viewFile } from '@/lib/open-file'
import { callPortal, uploadToPortal, type Portal, type PortalFile, type PortalInvoice, type PortalLine, type PortalQuote } from './api'

/**
 * The couple's portal, /p/<token> (28 Oct 2026). Like Studio Ninja's: one page
 * with every document in tabs, when each was sent and paid, the next payment
 * due, and a place to upload their own files.
 *
 * No login: the private link is the key, as with the crew agreement and the
 * galleries. Jena opens the same page from a project with ?preview=1, which
 * records nothing (no "opened" dates, no To do note for her own uploads).
 */

type Tab = 'overview' | 'quote' | 'invoices' | 'files'

const TAB_LABEL: Record<Tab, string> = { overview: 'Overview', quote: 'Quote', invoices: 'Invoices', files: 'Files' }

const INVOICE_KIND: Record<PortalInvoice['kind'], string> = {
  booking: 'Booking invoice',
  deposit: 'Deposit invoice',
  balance: 'Balance invoice',
  additional: 'Invoice',
  standard: 'Invoice',
}

export function ClientPortalPage() {
  const { token = '' } = useParams()
  const [search] = useSearchParams()
  const preview = search.get('preview') === '1'
  const portal = useQuery({
    queryKey: ['client-portal', token],
    queryFn: () => callPortal<{ portal: Portal }>({ token, preview }).then((data) => data.portal),
    retry: false,
    refetchOnWindowFocus: false,
  })

  const initial = (window.location.hash.slice(1) || 'overview') as Tab
  const [tab, setTabState] = useState<Tab>(initial in TAB_LABEL ? initial : 'overview')
  const setTab = (next: Tab) => {
    setTabState(next)
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${next}`)
    window.scrollTo({ top: 0 })
  }

  // Documents Jena uploaded as PDFs (a Studio Ninja contract, say) show with
  // the documents on the overview, not under Files (28 Oct 2026).
  const uploads = portal.data?.files.filter((file) => file.slot) ?? []
  const data = portal.data ? { ...portal.data, files: portal.data.files.filter((file) => !file.slot) } : undefined
  const couple = data ? data.project.people.map((person) => person.firstName).join(' & ') || data.project.title || 'Your project' : ''

  useEffect(() => {
    if (couple) document.title = `${couple} · De Leeuw Photo & Film`
  }, [couple])

  const tabs: Tab[] = data
    ? (['overview', data.quotes.length > 0 ? 'quote' : null, data.invoices.length > 0 ? 'invoices' : null, 'files'].filter(Boolean) as Tab[])
    : []

  return (
    <div data-register="portal" className="min-h-dvh bg-paper text-ink">
      {preview ? (
        <p className="bg-ink px-4 py-2 text-center text-[length:var(--text-xs)] text-cream">
          Preview: this is exactly what the couple sees. Your visits here are not counted as theirs.
        </p>
      ) : null}

      <div className="mx-auto flex max-w-[52rem] flex-col px-4 pb-16 sm:px-6">
        <header className="flex flex-col items-center gap-3 pt-10 pb-6 text-center">
          <Lion size={36} />
          <p className="label tracking-[0.12em] uppercase">De Leeuw Photo &amp; Film</p>
          {data ? (
            <>
              <h1 className="display text-[2rem] leading-tight sm:text-[2.5rem]">{couple}</h1>
              <p className="text-[length:var(--text-sm)] text-taupe">
                {formatDateLong(data.project.eventDate)}
                {data.project.venue ? ` · ${data.project.venue}` : ''}
              </p>
            </>
          ) : null}
        </header>

        {portal.isPending ? <p className="py-10 text-center text-taupe">Opening your page…</p> : null}
        {portal.isError ? <p className="card p-6 text-center text-clay">{portal.error.message}</p> : null}

        {data ? (
          <>
            <nav aria-label="Your documents" className="sticky top-0 z-10 -mx-4 mb-6 bg-paper/95 px-4 backdrop-blur-sm sm:mx-0 sm:px-0">
              <ul className="flex gap-1 overflow-x-auto border-b">
                {tabs.map((key) => (
                  <li key={key}>
                    <button
                      type="button"
                      onClick={() => setTab(key)}
                      aria-current={tab === key ? 'page' : undefined}
                      className={cn(
                        '-mb-px border-b-2 px-4 py-3 text-[length:var(--text-sm)] whitespace-nowrap transition-colors',
                        tab === key ? 'border-ink font-semibold text-ink' : 'border-transparent text-taupe hover:text-ink',
                      )}
                    >
                      {TAB_LABEL[key]}
                      {key === 'invoices' && data.invoices.length > 1 ? ` (${data.invoices.length})` : ''}
                      {key === 'files' && data.files.length > 0 ? ` (${data.files.length})` : ''}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {tab === 'overview' || !tabs.includes(tab) ? <Overview portal={data} uploads={uploads} token={token} preview={preview} onOpen={setTab} /> : null}
            {tab === 'quote' && tabs.includes('quote') ? <Quotes portal={data} token={token} preview={preview} couple={couple} /> : null}
            {tab === 'invoices' && tabs.includes('invoices') ? <Invoices portal={data} token={token} preview={preview} couple={couple} /> : null}
            {tab === 'files' ? <Files portal={data} token={token} preview={preview} /> : null}

            <footer className="mt-12 flex flex-col items-center gap-1 text-center text-[length:var(--text-xs)] text-taupe">
              <p>Questions? Jenalee is a message away.</p>
              <p className="flex flex-wrap justify-center gap-x-3">
                {data.business?.email ? (
                  <a href={`mailto:${data.business.email}`} className="underline underline-offset-4">
                    {data.business.email}
                  </a>
                ) : null}
                {data.business?.phone ? <a href={`tel:${data.business.phone}`}>{data.business.phone}</a> : null}
                {data.business?.website ? (
                  <a href={data.business.website} target="_blank" rel="noreferrer noopener" className="underline underline-offset-4">
                    {data.business.website.replace(/^https?:\/\/(www\.)?/, '')}
                  </a>
                ) : null}
              </p>
            </footer>
          </>
        ) : null}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

const UPLOAD_TITLE: Record<NonNullable<PortalFile['slot']>, string> = {
  quote: 'Quote',
  invoice: 'Invoice',
  contract: 'Contract',
  questionnaire: 'Questionnaire',
}

function Overview({
  portal,
  uploads,
  token,
  preview,
  onOpen,
}: {
  portal: Portal
  uploads: PortalFile[]
  token: string
  preview: boolean
  onOpen: (tab: Tab) => void
}) {
  const payment = nextPayment(portal.invoices)
  const owed = portal.invoices.reduce((sum, invoice) => sum + outstanding(invoice), 0)
  const quote = portal.quotes[0]

  return (
    <div className="flex flex-col gap-4">
      {portal.invoices.length > 0 ? (
        <Card>
          <p className="label">Next payment</p>
          {payment ? (
            <>
              <p className="display mt-1 text-[1.75rem]" data-numeric>
                {formatMoney(payment.amount)}
              </p>
              <p className="text-[length:var(--text-sm)] text-taupe">
                {payment.label} · due {formatDateShort(payment.dueDate)}
                {payment.overdue ? <span className="text-clay"> · overdue</span> : null}
              </p>
              <button type="button" onClick={() => onOpen('invoices')} className="mt-3 text-[length:var(--text-sm)] underline underline-offset-4">
                See the invoice and banking details
              </button>
            </>
          ) : (
            <p className="mt-1 text-[length:var(--text-base)] text-sage">{owed > 0 ? 'Nothing due right now.' : 'All paid. Thank you!'}</p>
          )}
        </Card>
      ) : null}

      <Card>
        <p className="label mb-2">Your booking</p>
        <dl className="flex flex-col">
          <Row label="Date">{formatDateLong(portal.project.eventDate)}</Row>
          {portal.project.venue ? <Row label="Venue">{portal.project.venue}</Row> : null}
          {portal.project.package ? <Row label="Collection">{portal.project.package}</Row> : null}
          {portal.project.contractSignedAt ? <Row label="Contract">Signed {formatDayOf(portal.project.contractSignedAt)}</Row> : null}
        </dl>
      </Card>

      {portal.deliverables.length > 0 ? (
        <Card>
          <p className="label mb-2">What you'll receive</p>
          <ul className="flex flex-col">
            {portal.deliverables.map((item) => (
              <li key={item.title} className="flex items-center justify-between gap-3 border-b py-2.5 text-[length:var(--text-sm)] last:border-b-0">
                <span>{item.title}</span>
                {item.delivered ? <span className="text-[length:var(--text-xs)] text-sage">Delivered</span> : null}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Card>
        <p className="label mb-2">Your documents</p>
        <ul className="flex flex-col">
          {quote ? (
            <DocLink onClick={() => onOpen('quote')} title={`Quote ${quote.quote_number}`} detail={quoteFacts(quote)} />
          ) : null}
          {portal.invoices.map((invoice) => (
            <DocLink key={invoice.id} onClick={() => onOpen('invoices')} title={INVOICE_KIND[invoice.kind]} detail={invoiceFacts(invoice)} />
          ))}
          {(portal.questionnaires ?? []).map((questionnaire) => (
            <DocLink
              key={questionnaire.token}
              onClick={() => window.location.assign(`/q/${questionnaire.token}${preview ? '?preview=1' : ''}`)}
              title={questionnaire.name}
              detail={questionnaire.completedAt ? `Sent in ${formatDayOf(questionnaire.completedAt)}, thank you` : 'Please fill this in closer to your wedding'}
            />
          ))}
          {uploads.map((file) => (
            <DocLink
              key={file.id}
              onClick={() => void viewFile(() => callPortal<{ url: string }>({ token, file: file.id }).then((data) => data.url))}
              title={file.slot ? UPLOAD_TITLE[file.slot] : file.name}
              detail={`${file.name} · ${formatDayOf(file.uploaded_at)}`}
            />
          ))}
          <DocLink
            onClick={() => onOpen('files')}
            title="Files"
            detail={portal.files.length > 0 ? `${portal.files.length} ${portal.files.length === 1 ? 'file' : 'files'}` : 'Add your inspiration, family list or timeline'}
          />
        </ul>
      </Card>
    </div>
  )
}

/** The next instalment still owed across their invoices, oldest due first. */
function nextPayment(invoices: PortalInvoice[]) {
  const on = today()
  const due = invoices.flatMap((invoice) => {
    const label = INVOICE_KIND[invoice.kind]
    if (invoice.instalments.length === 0) {
      const left = outstanding(invoice)
      return left > 0 ? [{ amount: left, dueDate: invoice.due_date, label, overdue: invoice.due_date < on }] : []
    }
    const next = nextDue(applyPayments(invoice.instalments, invoice.amount_paid, on))
    return next ? [{ amount: next.outstanding, dueDate: next.dueDate, label: next.label, overdue: next.status === 'overdue' }] : []
  })
  return due.sort((a, b) => a.dueDate.localeCompare(b.dueDate))[0] ?? null
}

function quoteFacts(quote: PortalQuote): string {
  return [
    quote.sent_at ? `Received ${formatDayOf(quote.sent_at)}` : null,
    quote.accepted_at ? `accepted ${formatDayOf(quote.accepted_at)}` : quote.valid_until ? `valid until ${formatDateShort(quote.valid_until)}` : null,
  ]
    .filter(Boolean)
    .join(', ')
}

function invoiceFacts(invoice: PortalInvoice): string {
  return [
    `Received ${invoice.sent_at ? formatDayOf(invoice.sent_at) : formatDateShort(invoice.issue_date)}`,
    invoice.status === 'paid' ? `paid${invoice.paid_at ? ` ${formatDayOf(invoice.paid_at)}` : ''}` : `${formatMoney(outstanding(invoice))} to pay`,
  ].join(', ')
}

// ---------------------------------------------------------------------------
// Quote
// ---------------------------------------------------------------------------

function useMarkViewed(token: string, preview: boolean, kind: 'quote' | 'invoice', id: string | undefined) {
  const seen = useRef(new Set<string>())
  useEffect(() => {
    if (!id || seen.current.has(id)) return
    seen.current.add(id)
    void callPortal({ token, preview, viewed: { kind, id } }).catch(() => undefined)
  }, [token, preview, kind, id])
}

function Quotes({ portal, token, preview, couple }: { portal: Portal; token: string; preview: boolean; couple: string }) {
  const [index, setIndex] = useState(0)
  const quote = portal.quotes[index] ?? portal.quotes[0]
  useMarkViewed(token, preview, 'quote', quote?.id)
  if (!quote) return null
  const name = `Quote ${portal.business?.quotePrefix ?? ''}${quote.quote_number}`

  return (
    <div className="flex flex-col gap-4">
      {portal.quotes.length > 1 ? <Picker items={portal.quotes.map((item) => `Quote ${item.quote_number}`)} index={index} onPick={setIndex} /> : null}
      <Card>
        <DocHeader
          title={name}
          facts={[
            quote.sent_at ? `Received ${formatDayOf(quote.sent_at)}` : null,
            quote.accepted_at ? `Accepted ${formatDayOf(quote.accepted_at)}` : quote.valid_until ? `Valid until ${formatDateShort(quote.valid_until)}` : null,
          ]}
          actions={<PdfButtons token={token} kind="quote" id={quote.id} filename={fileName(couple, name)} />}
        />
        {quote.cover_note ? <p className="mt-4 text-[length:var(--text-sm)] leading-relaxed whitespace-pre-wrap">{quote.cover_note}</p> : null}
        <Lines lines={quote.line_items} />
        <Totals subtotal={quote.subtotal} vat={quote.vat} total={quote.total} vatRegistered={portal.business?.vatRegistered ?? false} />
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------

function Invoices({ portal, token, preview, couple }: { portal: Portal; token: string; preview: boolean; couple: string }) {
  const firstOpen = Math.max(
    0,
    portal.invoices.findIndex((invoice) => invoice.status !== 'paid'),
  )
  const [index, setIndex] = useState(firstOpen)
  const invoice = portal.invoices[index] ?? portal.invoices[0]
  useMarkViewed(token, preview, 'invoice', invoice?.id)
  if (!invoice) return null

  const reference = formatInvoiceNumber(portal.business?.invoicePrefix ?? 'DLC', invoice.invoice_number)
  const left = outstanding(invoice)
  const schedule = applyPayments(invoice.instalments, invoice.amount_paid, today())
  const bank = portal.business?.bank
  const hasBank = Boolean(bank?.accountNumber)

  return (
    <div className="flex flex-col gap-4">
      {portal.invoices.length > 1 ? (
        <Picker items={portal.invoices.map((item) => `${INVOICE_KIND[item.kind]} ${formatInvoiceNumber(portal.business?.invoicePrefix ?? 'DLC', item.invoice_number)}`)} index={index} onPick={setIndex} />
      ) : null}
      <Card>
        <DocHeader
          title={`${INVOICE_KIND[invoice.kind]} ${reference}`}
          facts={[
            `Received ${invoice.sent_at ? formatDayOf(invoice.sent_at) : formatDateShort(invoice.issue_date)}`,
            invoice.status === 'paid' ? `Paid${invoice.paid_at ? ` ${formatDayOf(invoice.paid_at)}` : ''}` : `Due ${formatDateShort(invoice.due_date)}`,
          ]}
          actions={<PdfButtons token={token} kind="invoice" id={invoice.id} filename={fileName(couple, `Invoice ${reference}`)} />}
        />
        <Lines lines={invoice.line_items} />
        <Totals subtotal={invoice.subtotal} vat={invoice.vat} total={invoice.total} vatRegistered={portal.business?.vatRegistered ?? false} />
        <dl className="mt-1 flex flex-col">
          <Row label="Paid so far">{formatMoney(Number(invoice.amount_paid))}</Row>
          <Row label="Still to pay">
            <span className={left > 0 ? 'font-semibold' : 'text-sage'}>{left > 0 ? formatMoney(left) : 'Nothing: paid in full'}</span>
          </Row>
        </dl>
      </Card>

      {schedule.length > 0 ? (
        <Card>
          <p className="label mb-2">Payment plan</p>
          <ul className="flex flex-col">
            {schedule.map((step) => (
              <li key={`${step.label}-${step.dueDate}`} className="flex items-baseline justify-between gap-3 border-b py-2.5 text-[length:var(--text-sm)] last:border-b-0">
                <span>
                  {step.label}
                  <span className="block text-[length:var(--text-xs)] text-taupe">Due {formatDateShort(step.dueDate)}</span>
                </span>
                <span className="text-right" data-numeric>
                  {formatMoney(step.amount)}
                  <span
                    className={cn(
                      'block text-[length:var(--text-xs)]',
                      step.status === 'paid' ? 'text-sage' : step.status === 'overdue' ? 'text-clay' : 'text-taupe',
                    )}
                  >
                    {step.status === 'paid' ? 'Paid' : step.status === 'overdue' ? 'Overdue' : step.status === 'part_paid' ? `${formatMoney(step.outstanding)} left` : 'To pay'}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {left > 0 && hasBank && bank ? (
        <Card>
          <p className="label mb-2">How to pay</p>
          <dl className="flex flex-col">
            {bank.accountName ? <Row label="Account name">{bank.accountName}</Row> : null}
            {bank.bankName ? <Row label="Bank">{bank.bankName}</Row> : null}
            <Row label="Account number">{bank.accountNumber}</Row>
            {bank.accountType ? <Row label="Account type">{bank.accountType}</Row> : null}
            {bank.branchCode ? <Row label="Branch code">{bank.branchCode}</Row> : null}
            <Row label="Reference">{reference}</Row>
          </dl>
        </Card>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Files
// ---------------------------------------------------------------------------

function Files({ portal, token, preview }: { portal: Portal; token: string; preview: boolean }) {
  const queryClient = useQueryClient()
  const input = useRef<HTMLInputElement>(null)
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['client-portal', token] })
  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      for (const file of files) await uploadToPortal(token, file, preview)
    },
    onSettled: refresh,
  })
  const remove = useMutation({
    mutationFn: (fileId: string) => callPortal({ token, preview, remove: fileId }),
    onSettled: refresh,
  })
  const fileUrl = (id: string) => () => callPortal<{ url: string }>({ token, file: id }).then((data) => data.url)

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-semibold">Share a file with Jenalee</p>
            <p className="text-[length:var(--text-sm)] text-taupe">Inspiration, your family photo list, the timeline. PDFs, photos, Word or Excel, up to 20 MB each.</p>
          </div>
          <input
            ref={input}
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx,.xls,.xlsx,.txt,.csv"
            className="hidden"
            onChange={(event) => {
              const chosen = Array.from(event.target.files ?? [])
              event.target.value = ''
              if (chosen.length > 0) upload.mutate(chosen)
            }}
          />
          <PortalButton primary busy={upload.isPending} onClick={() => input.current?.click()}>
            {upload.isPending ? 'Uploading…' : 'Upload files'}
          </PortalButton>
        </div>
        {upload.isError ? <p className="mt-3 text-[length:var(--text-sm)] text-clay">{upload.error.message}</p> : null}
        {upload.isSuccess ? <p className="mt-3 text-[length:var(--text-sm)] text-sage">Thank you, Jenalee has your files.</p> : null}
      </Card>

      {portal.files.length > 0 ? (
        <Card>
          <ul className="flex flex-col">
            {portal.files.map((file) => (
              <li key={file.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b py-3 last:border-b-0">
                <div className="min-w-0 flex-1">
                  <p className="text-[length:var(--text-sm)] break-all">{file.name}</p>
                  <p className="text-[length:var(--text-xs)] text-taupe">
                    {file.added_by === 'couple' ? 'From you' : 'From Jenalee'} · {formatDayOf(file.uploaded_at)}
                    {file.size_bytes ? ` · ${formatBytes(file.size_bytes)}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <FileAction label="View" onClick={() => viewFile(fileUrl(file.id))} />
                  <FileAction label="Download" onClick={() => downloadFile(fileUrl(file.id), file.name)} />
                  {file.added_by === 'couple' ? (
                    <button
                      type="button"
                      className="px-2 py-1 text-[length:var(--text-xs)] text-taupe hover:text-clay"
                      onClick={() => {
                        if (window.confirm(`Remove "${file.name}"?`)) remove.mutate(file.id)
                      }}
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function Card({ children }: { children: ReactNode }) {
  return <section className="card p-5 sm:p-6">{children}</section>
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-6 border-b py-2.5 last:border-b-0">
      <dt className="text-[length:var(--text-sm)] text-taupe">{label}</dt>
      <dd className="text-right text-[length:var(--text-sm)]">{children}</dd>
    </div>
  )
}

function DocLink({ title, detail, onClick }: { title: string; detail: string; onClick: () => void }) {
  return (
    <li className="border-b last:border-b-0">
      <button type="button" onClick={onClick} className="flex w-full items-center justify-between gap-3 py-3 text-left hover:opacity-70">
        <span>
          <span className="block text-[length:var(--text-sm)] font-semibold">{title}</span>
          <span className="block text-[length:var(--text-xs)] text-taupe">{detail}</span>
        </span>
        <span aria-hidden className="text-taupe">
          ›
        </span>
      </button>
    </li>
  )
}

function DocHeader({ title, facts, actions }: { title: string; facts: Array<string | null>; actions: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="display text-[1.375rem]">{title}</h2>
        <p className="text-[length:var(--text-sm)] text-taupe">{facts.filter(Boolean).join(' · ')}</p>
      </div>
      {actions}
    </div>
  )
}

function Picker({ items, index, onPick }: { items: string[]; index: number; onPick: (index: number) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((label, i) => (
        <button
          key={label}
          type="button"
          onClick={() => onPick(i)}
          className={cn(
            'rounded-full px-3.5 py-1.5 text-[length:var(--text-xs)] transition-colors',
            i === index ? 'bg-ink text-cream' : 'bg-[var(--color-wash-strong)] text-ink hover:bg-[var(--color-rule)]',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

function Lines({ lines }: { lines: PortalLine[] }) {
  return (
    <ul className="mt-5 flex flex-col border-t">
      {[...lines]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((line, index) => (
          <li key={`${line.description}-${index}`} className="flex items-baseline justify-between gap-4 border-b py-3">
            <span className="min-w-0 text-[length:var(--text-sm)]">
              {line.description}
              {line.detail ? <span className="block text-[length:var(--text-xs)] whitespace-pre-wrap text-taupe">{line.detail}</span> : null}
            </span>
            <span className="shrink-0 text-[length:var(--text-sm)]" data-numeric>
              {formatMoney(Number(line.line_total))}
            </span>
          </li>
        ))}
    </ul>
  )
}

function Totals({ subtotal, vat, total, vatRegistered }: { subtotal: number | string; vat: number | string; total: number | string; vatRegistered: boolean }) {
  return (
    <dl className="mt-2 flex flex-col">
      {vatRegistered ? (
        <>
          <Row label="Subtotal">{formatMoney(Number(subtotal))}</Row>
          <Row label="VAT">{formatMoney(Number(vat))}</Row>
        </>
      ) : null}
      <Row label="Total">
        <span className="font-semibold" data-numeric>
          {formatMoney(Number(total))}
        </span>
      </Row>
    </dl>
  )
}

function PortalButton({ children, onClick, primary = false, busy = false }: { children: ReactNode; onClick: () => void; primary?: boolean; busy?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={cn(
        'inline-flex h-10 items-center justify-center rounded-md px-4 text-[length:var(--text-sm)] font-medium whitespace-nowrap transition-opacity disabled:opacity-50',
        primary ? 'bg-ink text-cream hover:opacity-85' : 'bg-[var(--color-wash-strong)] text-ink hover:bg-[var(--color-rule)]',
      )}
    >
      {children}
    </button>
  )
}

function FileAction({ label, onClick }: { label: string; onClick: () => Promise<void> }) {
  const [state, setState] = useState<'idle' | 'busy' | 'error'>('idle')
  return (
    <button
      type="button"
      disabled={state === 'busy'}
      className={cn('rounded-md px-2.5 py-1.5 text-[length:var(--text-xs)] hover:bg-[var(--color-wash-strong)] disabled:opacity-50', state === 'error' && 'text-clay')}
      onClick={() => {
        setState('busy')
        onClick().then(
          () => setState('idle'),
          () => setState('error'),
        )
      }}
    >
      {state === 'busy' ? '…' : state === 'error' ? 'Try again' : label}
    </button>
  )
}

function PdfButtons({ token, kind, id, filename }: { token: string; kind: 'quote' | 'invoice'; id: string; filename: string }) {
  const url = () => callPortal<{ url: string }>({ token, pdf: { kind, id } }).then((data) => data.url)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  return (
    <div className="flex flex-col items-end gap-1">
      <PortalButton
        busy={busy}
        onClick={() => {
          setBusy(true)
          setFailed(false)
          downloadFile(url, `${filename}.pdf`)
            .catch(() => setFailed(true))
            .finally(() => setBusy(false))
        }}
      >
        {busy ? 'Preparing…' : 'Download PDF'}
      </PortalButton>
      {failed ? <span className="text-[length:var(--text-xs)] text-clay">That didn't work. Please try again.</span> : null}
    </div>
  )
}
